import { I18nLocale, t } from '@ardoq/i18n';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { IFilterParams } from 'ag-grid-enterprise';
import { APIComponentAttributes } from '@ardoq/api-types';
import { Multiselect, SelectOption } from '@ardoq/select';
import { InventoryContext } from '../types';
import { SetFilterEntry } from './types';
import {
  getFilterOptionLabel,
  hasValueSelectOption,
  noValueSelectOption,
} from './agGridFilterOperations';

type HasValueFilterProps = IFilterParams<
  APIComponentAttributes,
  InventoryContext
>;

const getOptions = (i18nLocale: I18nLocale): SelectOption<string>[] => [
  {
    label: getFilterOptionLabel(noValueSelectOption, i18nLocale),
    value: noValueSelectOption,
  },
  {
    label: getFilterOptionLabel(hasValueSelectOption, i18nLocale),
    value: hasValueSelectOption,
  },
];

export const HasValueFilter = ({
  api,
  column,
  context,
}: HasValueFilterProps) => {
  const colId = column.getColId();
  const [selectedValues, setSelectedValues] = useState<SelectOption<string>[]>(
    []
  );
  const selectedValuesRef = useRef(selectedValues);
  selectedValuesRef.current = selectedValues;
  const i18nLocale = context?.i18nLocale ?? {};

  const resolveInitialValues = useCallback(() => {
    const columnFilterModel = api.getColumnFilterModel<SetFilterEntry>(colId);
    const initialValues = (columnFilterModel?.values ?? []) as string[];
    const localeAtInvocation = context?.i18nLocale ?? {};

    setSelectedValues(
      initialValues.map(value => ({
        label: getFilterOptionLabel(value, localeAtInvocation),
        value,
      }))
    );
  }, [api, colId, context]);

  useEffect(() => {
    const handleFilterOpened = () => {
      resolveInitialValues();
    };

    const handleFilterChanged = () => {
      const columnFilterModel = api.getColumnFilterModel<SetFilterEntry>(colId);
      const filterValues = (columnFilterModel?.values as string[]) ?? [];

      const currentIds = selectedValuesRef.current.map(v => v.value);
      const hasChanged =
        filterValues.length !== currentIds.length ||
        !filterValues.every(id => currentIds.includes(id));

      if (hasChanged) {
        if (filterValues.length === 0) {
          setSelectedValues([]);
        } else {
          resolveInitialValues();
        }
      }
    };

    api.addEventListener('filterOpened', handleFilterOpened);
    api.addEventListener('filterChanged', handleFilterChanged);

    return () => {
      api.removeEventListener('filterOpened', handleFilterOpened);
      api.removeEventListener('filterChanged', handleFilterChanged);
    };
  }, [api, colId, resolveInitialValues]);

  const handleValueChange = async (
    values: SelectOption<string>[] | null
  ): Promise<void> => {
    const newValues = values ?? [];

    setSelectedValues(newValues);

    const nextFilter =
      newValues.length > 0
        ? ({
            values: newValues.map(v => v.value),
            filterType: 'set',
          } satisfies SetFilterEntry)
        : null;

    await api.setColumnFilterModel(colId, nextFilter);
    api.onFilterChanged();
  };

  return (
    <div style={{ padding: '10px', minWidth: '200px' }}>
      <Multiselect<string>
        placeholder={t(
          i18nLocale,
          'inventoryApp:ag.filter.hasValuePlaceholder',
          'Filter by value'
        )}
        options={getOptions(i18nLocale)}
        value={selectedValues}
        menuIsOpen={true}
        onChange={handleValueChange}
        autoFocus={true}
      />
    </div>
  );
};
